"use client";

import { useWindowScroll } from "@mantine/hooks";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

// ===========================
// Types
// ===========================

type IScrollDirection = "up" | "down" | "idle";

type IScreenSize = "sm" | "md" | "lg" | "xl" | "2xl";

type IUseScrollHeaderConfig = {
  scrollBufferPx?: number;
  throttleDelayMs?: number;
  headerSelector?: string;
  anchorElementId?: string;
  defaultTopPosition?: string | number;
  hideTopPosition?: string | number;
  enableOnScreenSize?: IScreenSize;
  onScrollUp?: () => void;
  onScrollDown?: () => void;
  resetOnPathChange?: boolean;
};

// ===========================
// Utilities
// ===========================

const SCREEN_BREAKPOINTS: Record<IScreenSize, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

const getMediaQuery = (size: IScreenSize): string => {
  return `(min-width: ${SCREEN_BREAKPOINTS[size]}px)`;
};

const normalizePosition = (position: string | number): string => {
  return typeof position === "number" ? `${position}px` : position;
};

// ===========================
// Generic Hook
// ===========================

/**
 * A custom React hook that implements a "headroom" effect for a header element.
 * The header hides when scrolling down past a certain buffer and reappears when scrolling up.
 * It can be anchored to another element (like a banner) and is responsive.
 *
 * @param {IUseScrollHeaderConfig} config - The configuration object for the hook.
 * @param {number} [config.scrollBufferPx=500] - The number of pixels to scroll down before the header starts hiding.
 * @param {number} [config.throttleDelayMs=100] - The minimum change in scroll position (in pixels) to trigger a direction update, acting as a pixel threshold (note: despite the `Ms` suffix, this is not a time delay).
 * @param {string} [config.headerSelector="header"] - The CSS selector for the header element to be controlled.
 * @param {string} [config.anchorElementId] - The ID of an element to which the header's position is anchored. If provided, the header's top position will be adjusted based on this element's height.
 * @param {string | number} [config.defaultTopPosition="0px"] - The default `top` CSS value for the header when it's visible. Can be a number (pixels) or a string.
 * @param {string | number} [config.hideTopPosition="0px"] - The `top` CSS value for the header when it's hidden. Can be a number (pixels) or a string.
 * @param {IScreenSize} [config.enableOnScreenSize="md"] - The minimum screen breakpoint at which the scroll effect should be enabled.
 * @param {() => void} [config.onScrollUp] - An optional callback function to execute when the user scrolls up.
 * @param {() => void} [config.onScrollDown] - An optional callback function to execute when the user scrolls down.
 * @param {boolean} [config.resetOnPathChange=true] - If true, the hook's internal state will reset when the URL path changes.
 *
 * @returns {{
 *   isAnchorVisible: boolean;
 *   setIsAnchorVisible: React.Dispatch<React.SetStateAction<boolean>>;
 *   scrollY: number;
 *   direction: IScrollDirection;
 *   isScrollingUp: boolean;
 *   isScrollingDown: boolean;
 * }} An object containing the hook's state:
 * - `isAnchorVisible`: State to control the visibility of the anchor element.
 * - `setIsAnchorVisible`: Function to update the `isAnchorVisible` state.
 * - `scrollY`: The current vertical scroll position of the window.
 * - `direction`: The current scroll direction ('up', 'down', or 'idle').
 * - `isScrollingUp`: A boolean indicating if the current scroll direction is 'up'.
 * - `isScrollingDown`: A boolean indicating if the current scroll direction is 'down'.
 *
 * @example
 * ```jsx
 * // In your component:
 * const MyComponent = () => {
 *   useScrollHeader({
 *     headerSelector: '#main-header',
 *     scrollBufferPx: 100,
 *     hideTopPosition: '-80px',
 *     enableOnScreenSize: 'lg',
 *   });
 *
 *   return (
 *     <div>
 *       <header id="main-header" style={{ position: 'fixed', top: 0, transition: 'top 0.3s' }}>
 *         My Header
 *       </header>
 *       <main style={{ paddingTop: '80px' }}>
 *         {...}
 *       </main>
 *     </div>
 *   );
 * };
 * ```
 */
export const useScrollHeader = ({
  scrollBufferPx = 500,
  throttleDelayMs = 100,
  headerSelector = "header",
  anchorElementId,
  defaultTopPosition = "0px",
  hideTopPosition = "0px",
  enableOnScreenSize = "md",
  onScrollUp,
  onScrollDown,
  resetOnPathChange = true,
}: IUseScrollHeaderConfig) => {
  const [isAnchorVisible, setIsAnchorVisible] = useState(true);
  const [direction, setDirection] = useState<IScrollDirection>("idle");
  const pathName = usePathname();
  const lastYRef = useRef(0);
  const rafIdRef = useRef<number>();

  const [{ y }] = useWindowScroll();

  // Memoized callbacks to prevent recreating on every render
  const handleScrollUp = useCallback(() => {
    onScrollUp?.();
  }, [onScrollUp]);

  const handleScrollDown = useCallback(() => {
    onScrollDown?.();
  }, [onScrollDown]);

  // Track scroll direction
  useEffect(() => {
    if (Math.abs(y - lastYRef.current) < throttleDelayMs) {
      return;
    }

    if (y > lastYRef.current) {
      setDirection("down");
      handleScrollDown();
    } else if (y < lastYRef.current) {
      setDirection("up");
      handleScrollUp();
    } else {
      setDirection("idle");
    }

    lastYRef.current = y;
  }, [y, throttleDelayMs, handleScrollDown, handleScrollUp]);

  // Update header position with RAF
  useEffect(() => {
    const header = document.querySelector(headerSelector) as HTMLElement;
    const anchorElement = anchorElementId
      ? document.getElementById(anchorElementId)
      : null;

    if (!header) return;

    const updateHeaderPosition = () => {
      // Cancel any pending RAF
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Schedule update with RAF for smooth rendering
      rafIdRef.current = requestAnimationFrame(() => {
        const isScreenSizeMatch = window.matchMedia(
          getMediaQuery(enableOnScreenSize),
        ).matches;

        const anchorHeight =
          anchorElement && isAnchorVisible ? anchorElement.offsetHeight : 0;

        // Calculate positions
        const calculatedDefaultTop =
          anchorHeight > 0
            ? `${anchorHeight}px`
            : normalizePosition(defaultTopPosition);
        const calculatedHideTop = normalizePosition(hideTopPosition);

        if (isScreenSizeMatch && isAnchorVisible) {
          if (y < scrollBufferPx) {
            // Stay at default position
            header.style.top = calculatedDefaultTop;
          } else {
            // Past buffer - headroom behavior
            if (direction === "down") {
              // Scrolling down - hide header
              header.style.top = calculatedHideTop;
            } else if (direction === "up") {
              // Scrolling up - show header
              header.style.top = calculatedDefaultTop;
            }
          }
        } else {
          // Screen size doesn't match or anchor hidden
          header.style.top = calculatedHideTop;
        }
      });
    };

    updateHeaderPosition();

    // Debounce resize for performance
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateHeaderPosition, 150);
    };

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      clearTimeout(resizeTimeout);
    };
  }, [
    y,
    direction,
    isAnchorVisible,
    scrollBufferPx,
    headerSelector,
    anchorElementId,
    defaultTopPosition,
    hideTopPosition,
    enableOnScreenSize,
  ]);

  // Reset on path change
  useEffect(() => {
    if (resetOnPathChange) {
      setIsAnchorVisible(true);
      lastYRef.current = 0;
      setDirection("idle");
    }
  }, [pathName, resetOnPathChange]);

  return {
    isAnchorVisible,
    setIsAnchorVisible,
    scrollY: y,
    direction,
    isScrollingUp: direction === "up",
    isScrollingDown: direction === "down",
  };
};
